import React from "react";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import "./Testimonials.css";

// ─── Testimonials — most recent first ─────────────────────────────────────
const TESTIMONIALS = [
  {
    id: "suzanne-klatten",
    type: "academic",
    name: "Suzanne Klatten, MA",
    initials: "SK",
    title: "Lecturer Communication — English & Personal Branding",
    institution: "Utrecht University of Applied Sciences",
    university: "HU Utrecht — Netherlands",
    date: "February 3, 2026",
    context: "International Graduate Programs & Scholarship Applications",
    keyQuote:
      "Ms. Rebai was a wonderful student to have in class. She was not afraid to step outside her comfort zone and within the months she was in my class, I saw significant improvements. I also saw her positive influence on group work, both on the group dynamic and the final result.",
    highlights: [
      "High-quality assignments demonstrating academic thinking and genuine creativity",
      "Asked questions showing real depth of understanding — always wanted to go further",
      "Not afraid of feedback; applied it diligently and improved continuously",
      "Positive influence on group dynamics and collective outcomes",
    ],
    accentColor: "100, 220, 160",
  },
  {
    id: "luc-vandijk",
    type: "academic",
    name: "Luc van Dijk-Wijmenga",
    initials: "LV",
    title: "Erasmus Coordinator — Minor International Communication",
    institution: "HU University of Applied Sciences Utrecht",
    university: "HU Utrecht — Netherlands",
    date: "February 3, 2026",
    context: "International Graduate Programs & Scholarship Applications",
    keyQuote:
      "Ela is a determined, positive, and open-minded student. Her participation in the Ad Venture competition highlighted her ability to work effectively in teams, contribute creatively, and perform under international and competitive conditions.",
    highlights: [
      "Demonstrated responsibility, initiative, and serious engagement throughout",
      "Excelled in Ad Venture international competition under competitive pressure",
      "Adapted smoothly to an international environment; enriched the learning community",
      "Strong academic ambitions in IT, data and AI — motivation and maturity commended",
    ],
    accentColor: "200, 130, 255",
  },
  {
    id: "nadia-bahri",
    type: "academic",
    name: "Nadia Bahri",
    initials: "NB",
    title: "English Teacher",
    institution: "Faculty of Economic Sciences and Management of Tunis",
    university: "University of Tunis El Manar — Tunisia",
    date: "April 14, 2025",
    context: "Erasmus+ Mobility Programme — Utrecht University, Netherlands",
    keyQuote:
      "I can say with confidence that she is one of the best students I have met over the years. Ela is a very driven and hard-working student of whom I think highly. Her enthusiasm, remarkable ability to learn, generosity, energy, and high standards make her an excellent student.",
    highlights: [
      "Superior performance: excellent attendance, grades and active class participation",
      "Outstanding oral English presentation skills — ranked top in class",
      "Excellent interpersonal skills and effective team member with great spirit",
      "Strongly recommended — would certainly meet all expectations and be a great asset",
    ],
    accentColor: "140, 180, 255",
  },
  {
    id: "nawel-choufa",
    type: "professional",
    name: "Nawel Choufa",
    initials: "NC",
    title: "Manager",
    institution: "Carthage International Moving (CIM)",
    university: "Tunis, Tunisia",
    date: "2024",
    context: "Company Website & Volume Calculator — Freelance Project",
    keyQuote:
      "Ela built our company website from scratch and delivered exactly what we needed — professional, clean, and functional. The interactive volume calculator she developed has genuinely improved how we serve our clients online. She was responsive, autonomous, and showed real initiative throughout the project.",
    highlights: [
      "Delivered a fully professional website independently with minimal oversight",
      "Built a custom interactive volume calculator that directly improved client experience",
      "Showed strong initiative and communicated effectively throughout the project",
      "The website has measurably improved our company's online presence and client inquiries",
    ],
    accentColor: "255, 160, 80",
  },
];

// ─── Star row ─────────────────────────────────────────────────────────────
const Stars = ({ color }) => (
  <div className="testi-stars" style={{ color: `rgba(${color}, 0.8)` }}>
    {[...Array(5)].map((_, i) => <FaStar key={i} />)}
  </div>
);

// ─── TestimonialCard ──────────────────────────────────────────────────────
const TestimonialCard = ({ t }) => (
  <div
    className={`testi-card testi-card--${t.type}`}
    style={{ "--testi-color": t.accentColor }}
  >
    {/* Avatar + meta */}
    <div className="testi-header">
      <div className="testi-avatar" style={{ background: `rgba(${t.accentColor}, 0.12)`, border: `1px solid rgba(${t.accentColor}, 0.28)`, color: `rgba(${t.accentColor}, 1)` }}>
        {t.initials}
      </div>
      <div className="testi-meta">
        <div className="testi-name">{t.name}</div>
        <div className="testi-title" style={{ color: `rgba(${t.accentColor}, 0.85)` }}>{t.title}</div>
        <div className="testi-institution">{t.institution}</div>
        <div className="testi-university">{t.university}</div>
      </div>
      <div className="testi-header-right">
        <span className={`testi-type-badge testi-type-badge--${t.type}`}>
          {t.type === "academic" ? "🎓 Academic" : "💼 Professional"}
        </span>
        <div className="testi-date">📅 {t.date}</div>
      </div>
    </div>

    {/* Context */}
    <div className="testi-context" style={{ borderColor: `rgba(${t.accentColor}, 0.18)`, color: `rgba(${t.accentColor}, 0.7)` }}>
      ✦ {t.context}
    </div>

    {/* Stars */}
    <Stars color={t.accentColor} />

    {/* Key quote */}
    <div className="testi-quote" style={{ borderLeftColor: `rgba(${t.accentColor}, 0.35)`, background: `rgba(${t.accentColor}, 0.04)` }}>
      <FaQuoteLeft className="testi-quote__icon" style={{ color: `rgba(${t.accentColor}, 0.5)` }} />
      <p className="testi-quote__text">{t.keyQuote}</p>
    </div>

    {/* Highlights */}
    <ul className="testi-highlights">
      {t.highlights.map((h, i) => (
        <li key={i} style={{ "--h-color": `rgba(${t.accentColor}, 0.8)` }}>{h}</li>
      ))}
    </ul>
  </div>
);

// ─── Testimonials ─────────────────────────────────────────────────────────
const Testimonials = () => (
  <div className="testimonials-section">
    <h2 className="section-title">Recommendations</h2>
    <p className="testi-intro">
      4 references — from a client, international lecturers at HU Utrecht, and a professor at the University of Tunis El Manar. Written for professional work, Erasmus+ mobility, and international graduate applications.
    </p>
    <div className="testi-list">
      {TESTIMONIALS.map((t) => (
        <TestimonialCard key={t.id} t={t} />
      ))}
    </div>
  </div>
);

export default Testimonials;
